import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getMuscleGroupCountsAndExercises } from "../services/homeService";
import { getExerciseLog, getWorkoutNotes, saveWorkoutNoteByDate } from "../utils/localStorageUtils";
import { pullAndMergeExerciseLog, pushExerciseLog } from "../services/syncService";
import { isClerkEnabled, useAuthCompat } from "../utils/clerkConfig";

export function useHomeData() {
    const location = useLocation();
    const [dates, setDates] = useState([]);
    const [muscleGroupCounts, setMuscleGroupCounts] = useState({});
    const [loggedExercises, setLoggedExercises] = useState([]);
    const [exerciseLog, setExerciseLog] = useState({});
    const [workoutNotes, setWorkoutNotes] = useState({});

    const { isLoaded, isSignedIn, getToken } = useAuthCompat();

    // Initial load (attempt pull only when auth is ready; then compute view)
    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                // Avoid hitting the API with an undefined token before Clerk loads
                if (!isClerkEnabled || (isLoaded && isSignedIn)) {
                    await pullAndMergeExerciseLog(getToken);
                }
            } catch (_) {
                // ignore sync errors; show local data
            }

            if (!isMounted) return;
            const { counts, todayExercises, lastDates } = getMuscleGroupCountsAndExercises();
            const log = getExerciseLog();
            const notes = getWorkoutNotes();

            setDates(lastDates);
            setMuscleGroupCounts(counts);
            setLoggedExercises(todayExercises);
            setExerciseLog(log);
            setWorkoutNotes(notes);
        })();

        return () => { isMounted = false; };
    }, [location, isLoaded, isSignedIn, getToken]);

    // When Clerk session becomes ready after SSO, pull again and refresh view
    useEffect(() => {
        if (!isLoaded || !isSignedIn) return;
        let isMounted = true;
        (async () => {
            try {
                await pullAndMergeExerciseLog(getToken);
            } catch (_) {}
            if (!isMounted) return;
            const { counts, todayExercises, lastDates } = getMuscleGroupCountsAndExercises();
            const log = getExerciseLog();
            const notes = getWorkoutNotes();
            setDates(lastDates);
            setMuscleGroupCounts(counts);
            setLoggedExercises(todayExercises);
            setExerciseLog(log);
            setWorkoutNotes(notes);
            // After successfully refreshing local state, best-effort push to ensure
            // any local changes made while signed-out get uploaded once auth is ready.
            try { await pushExerciseLog(getToken); } catch (_) {}
        })();
        return () => { isMounted = false; };
    }, [isLoaded, isSignedIn, getToken]);

    const updateWorkoutNote = async (date, noteText) => {
        saveWorkoutNoteByDate(date, noteText);
        setWorkoutNotes(getWorkoutNotes());
        try {
            await pushExerciseLog(getToken);
        } catch (_) {
            // best-effort sync
        }
    };

    return {
        dates,
        muscleGroupCounts,
        loggedExercises,
        exerciseLog,
        workoutNotes,
        updateWorkoutNote,
    };
}
